/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useRef, memo } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import Row from './Row';
import Loader from '../Loader';
import Tooltip from '../Tooltip';
import TotalRow from './TotalRow';
import DropdownFilter from '../DropdownFilter';

/* const areEqual = (prevProps, nextProps) => {
  // Object to store the changed props
  const changedProps = {};

  // Create a set of all keys in both previous and next props
  const allKeys = new Set([
    ...Object.keys(prevProps),
    ...Object.keys(nextProps),
  ]);

  // Iterate over all keys to check for differences
  allKeys.forEach((key) => {
    if (prevProps[key] !== nextProps[key]) {
      changedProps[key] = {
        previous: prevProps[key],
        next: nextProps[key],
      };
    }
  });

  // If there are any differences, log them and return false so that React.memo triggers a re-render.
  if (Object.keys(changedProps).length > 0) {
    console.log('Changed props:', changedProps);
    return false;
  }

  // Otherwise, props are equal and we skip re-rendering.
  return true;
}; */

function Table({
  cols,
  rows = [],
  title,
  minHeight,
  loaderTop,
  headerRightContent,
  headerLeftContent,
  noCount,
  compact,
  total,
  reference,
  colsWithTotals,
  totalTitle,
  rowStyle,
  onRowClick,
  filterOptions,
  filterLabel,
  filterValue,
  onFilterChange,
}) {
  const tableRef = useRef();

  const { t } = useTranslation();

  // Disable the loader for the table as global loader is used
  const isFetching = false;

  const hasHeaderTooltip = cols?.some((col) => !!col.headerTooltip);

  return (
    <div
      className={classNames(
        'bg-white dark:bg-slate-800 flex-1 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 relative !overflow-x-auto',
        {
          'md:overflow-x-visible': hasHeaderTooltip,
        }
      )}
      style={{ minHeight }}
    >
      <header
        className={classNames('flex px-5 py-4 items-center', {
          'justify-between': !!headerRightContent,
        })}
      >
        <h2 className="font-semibold text-slate-800 dark:text-slate-100">
          {!noCount && t('common.All')} {title}{' '}
          {!noCount && (
            <span className="text-slate-400 dark:text-slate-500 font-medium">
              {total || rows?.length}
            </span>
          )}
        </h2>
        {filterOptions && (
          <div className="pl-4 flex items-center gap-2 text-sm font-bold">
            {filterLabel && <span>{filterLabel}</span>}
            <DropdownFilter
              filterValue={filterValue}
              onFilterChange={onFilterChange}
              options={filterOptions}
            />
          </div>
        )}
        {headerLeftContent && (
          <div className="flex items-center">{headerLeftContent}</div>
        )}
        {headerRightContent && (
          <div className="flex items-center">{headerRightContent}</div>
        )}
      </header>
      <div
        className="relative"
        style={{
          overflow: hasHeaderTooltip ? '' : 'hidden',
        }}
      >
        {isFetching && !rows.length && (
          <div
            style={{
              top: loaderTop || 256,
            }}
            className="absolute w-full flex justify-center items-center"
          >
            <Loader />
          </div>
        )}
        {/* Table */}
        <div
          className={
            hasHeaderTooltip ? '' : 'overflow-x-auto overflow-y-visible'
          }
        >
          <table
            className="table-auto w-full dark:text-slate-300"
            ref={reference || tableRef}
          >
            {/* Table header */}
            <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
              <tr>
                {cols.map((col) => {
                  if (col.isColumnHidden) {
                    return null;
                  }

                  return (
                    <th
                      key={col.title}
                      className={classNames(
                        'first:pl-5 last:pr-5 py-3 whitespace-nowrap',
                        {
                          'px-1 text-[10px]': compact,
                          'px-2': !compact,
                        }
                      )}
                    >
                      <div className="flex gap-2">
                        <div className="font-semibold text-left ">
                          {col.title}
                        </div>{' '}
                        {col.headerTooltip &&
                          typeof col.headerTooltip === 'function' &&
                          col.headerTooltip({
                            rows,
                            cols,
                          }) && (
                            <Tooltip position="left" size="small">
                              <div className="font-normal normal-case text-base">
                                {col.headerTooltip({
                                  rows,
                                  cols,
                                })}
                              </div>
                            </Tooltip>
                          )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            {/* Table body */}
            <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
              {rows?.map?.((row) => {
                return row ? (
                  <Row
                    compact={compact}
                    key={row?.id}
                    row={row}
                    cols={cols}
                    rowStyle={rowStyle}
                    onRowClick={onRowClick}
                  />
                ) : null;
              })}
              <TotalRow
                cols={cols}
                colsWithTotals={colsWithTotals}
                tableRef={reference || tableRef}
                totalTitle={totalTitle}
                rows={rows}
              />
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default memo(Table);
